import React, { useState } from 'react';

const SimpleWebexAuth = ({ onSubmit }) => {
  const [accessToken, setAccessToken] = useState('');
  const [orgId, setOrgId] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rawNetworkData, setRawNetworkData] = useState('');

  const webexTimezones = [
    'America/New_York',
    'America/Chicago', 
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Australia/Sydney'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!accessToken.trim()) {
      setError('Access token is required');
      return;
    }

    if (!orgId.trim()) {
      setError('Organization ID is required');
      return;
    }

    setLoading(true);
    
    try {
      // Create auth data object - includes both access_token and org_id
      const authData = {
        org_id: orgId.trim(),
        access_token: accessToken.trim(),
        timezone: timezone,
        token_type: 'Bearer',
        expires_at: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
      };

      await onSubmit(authData);
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWebex = () => {
    window.open('https://admin.webex.com/', '_blank', 'noopener,noreferrer');
  };

  // Smart parser for network response data
  const parseNetworkData = (data) => {
    try {
      // Try to parse as JSON first
      const jsonData = JSON.parse(data);
      
      // Look for access_token in various possible locations
      const token = jsonData.access_token || 
                   jsonData.accessToken || 
                   jsonData.token ||
                   jsonData.data?.access_token ||
                   jsonData.data?.accessToken;
                   
      // Look for org_id in various possible locations  
      let org = jsonData.org_id ||
                jsonData.orgId ||
                jsonData.organizationId ||
                jsonData.data?.org_id ||
                jsonData.data?.orgId;

      return { token, org };
    } catch (e) {
      // If not JSON, try regex patterns
      const tokenMatch = data.match(/"access_token"\s*:\s*"([^"]+)"/);
      const orgMatch = data.match(/"org_id"\s*:\s*"([^"]+)"/);
      
      let token = tokenMatch ? tokenMatch[1] : null;
      let org = orgMatch ? orgMatch[1] : null;
      
      return { token, org };
    }
  };;

  const handleSmartParse = () => {
    if (!rawNetworkData.trim()) {
      setError('Please paste the network response data');
      return;
    }

    const parsed = parseNetworkData(rawNetworkData);
    
    if (parsed.token) {
      setAccessToken(parsed.token);
      setError('');
    }
    
    if (parsed.org) {
      setOrgId(parsed.org);
    }
    
    if (!parsed.token && !parsed.org) {
      setError('Could not find access_token or org_id in the pasted data. Please check the response format.');
    } else {
      setError('');
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Inter, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        width: '100%',
        padding: '40px',
        boxSizing: 'border-box'
      }}>
        <h1 style={{
          fontSize: '2rem',
          marginBottom: '10px',
          color: '#333',
          textAlign: 'center'
        }}>
          Webex Control Hub Authentication
        </h1>
        
        <p style={{
          color: '#666',
          textAlign: 'center',
          marginBottom: '30px',
          fontSize: '1.1rem'
        }}>
          Capture access token from browser network logs
        </p>

        {/* Instructions */}
        <div style={{
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ marginTop: 0, color: '#0066cc' }}>Instructions:</h3>
          <ol style={{ color: '#333', lineHeight: '1.6' }}>
            <li><strong>Log out</strong> of any existing Webex sessions in your browser</li>
            <li><strong>Open Developer Tools</strong> (F12) and go to Network tab</li>
            <li><strong>Clear network logs</strong> and filter by "access_token"</li>
            <li>Click the button below to open Webex Control Hub</li>
            <li><strong>Log in fresh</strong> to https://admin.webex.com/</li>
            <li><strong>Find the network request</strong> containing "access_token"</li>
            <li><strong>Copy the access token</strong> and org ID from the response</li>
            <li><strong>Paste values below</strong> and submit</li>
          </ol>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <button
            type="button"
            onClick={handleOpenWebex}
            style={{
              padding: '15px 30px',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'background-color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0052a3'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#0066cc'}
          >
            🚀 Open Webex Control Hub
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            border: '1px solid #ffcdd2',
            color: '#c62828',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Smart Parser Section */}
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ marginTop: 0, color: '#495057' }}>🤖 Smart Parser</h3>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Paste the entire network response and let us extract the token and org ID automatically!
          </p>
          
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="rawNetworkData" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Raw Network Response:
            </label>
            <textarea
              id="rawNetworkData"
              value={rawNetworkData}
              onChange={(e) => setRawNetworkData(e.target.value)}
              placeholder='Paste the entire response JSON here, e.g.:
{
  "access_token": "eyJhbGciOiJSUzI1NiIs...",
  "org_id": "Y2lzY29zcGFyazovL3Vz..."
}'
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <button
            type="button"
            onClick={handleSmartParse}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#138496'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#17a2b8'}
          >
            🔍 Auto-Extract Token & Org ID
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="accessToken" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Access Token:
            </label>
            <textarea
              id="accessToken"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              required
              placeholder="Access token will appear here after smart parsing, or paste manually..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="orgId" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Organization ID:
            </label>
            <input
              type="text"
              id="orgId"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              placeholder="Enter your Webex organization ID..."
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label htmlFor="timezone" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Timezone:
            </label>
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'white'
              }}
            >
              {webexTimezones.map(tz => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: loading ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.3s ease'
            }}
          >
            {loading ? 'Authenticating...' : '✅ Submit Authentication'}
          </button>
        </form>

        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <h4 style={{ marginTop: 0, color: '#333' }}>Need Help Finding the Access Token?</h4>
          <p>1. In Network tab, look for requests to endpoints containing "access_token"</p>
          <p>2. Check the Response payload for "access_token" field</p>
          <p>3. Also look for "orgId" or "org_id" in the same response</p>
          <p>4. The token typically starts with "Bearer " or is a long JWT string</p>
        </div>
      </div>
    </div>
  );
};

export default SimpleWebexAuth;